import {
  serverTimestamp,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';
import type {
  NodePosition,
  RunCanvasOverrides,
  RunDayDivider,
  RunDoc,
  TaskDoc,
} from '@mct/shared';
import { runRef } from './firestore';

/**
 * Resolve the canvas (x, y) to render a task at within the given run.
 *
 * Order of precedence:
 *   1. If the run has been seeded (`canvasOverrides.seededAt != null`)
 *      and a phase-specific position exists → use it.
 *   2. If the run has been seeded but this task post-dates the seed
 *      (no entry in the override map) → fall back to `task.position`.
 *   3. If the run has not been seeded → the project-wide template
 *      (`task.position`).
 */
export function resolveTaskPosition(
  task: Pick<TaskDoc, 'taskId' | 'position'>,
  run: Pick<RunDoc, 'canvasOverrides'> | null | undefined,
): NodePosition {
  const overrides = run?.canvasOverrides;
  if (!overrides?.seededAt) return task.position;
  return overrides.taskPositions[task.taskId] ?? task.position;
}

/**
 * Whether the user is currently free to drag tasks / edit dividers
 * for this run. Past (completed) runs are read-only by design — even
 * editor+ users cannot mutate their layout.
 */
export function canEditRunCanvas(
  run: Pick<RunDoc, 'status'> | null | undefined,
): boolean {
  if (!run) return false;
  return run.status !== 'completed';
}

export function isRunSeeded(
  run: Pick<RunDoc, 'canvasOverrides'> | null | undefined,
): boolean {
  return !!run?.canvasOverrides?.seededAt;
}

/**
 * Initial seed: write the current `task.position` for every task into
 * the run's override map and stamp `seededAt`. Idempotent — calling
 * twice is a no-op (the second call overwrites with the same data,
 * but UI gates the button by `isRunSeeded`).
 *
 * Day dividers are preserved across the seed (they may pre-exist).
 */
export async function seedRunCanvas(
  db: Firestore,
  pid: string,
  runId: string,
  tasks: readonly Pick<TaskDoc, 'taskId' | 'position'>[],
  existing: RunCanvasOverrides | undefined,
): Promise<void> {
  const taskPositions: Record<string, NodePosition> = {};
  for (const t of tasks) {
    taskPositions[t.taskId] = { x: t.position.x, y: t.position.y };
  }
  await updateDoc(runRef(db, pid, runId), {
    canvasOverrides: {
      taskPositions,
      seededAt: serverTimestamp(),
      dayDividers: existing?.dayDividers ?? [],
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update a single task's position inside the run's override map.
 * Caller must verify the run is already seeded before invoking.
 */
export async function updateRunTaskPosition(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  position: NodePosition,
): Promise<void> {
  await updateDoc(runRef(db, pid, runId), {
    [`canvasOverrides.taskPositions.${taskId}`]: {
      x: position.x,
      y: position.y,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Replace the divider list wholesale. Day dividers are written as one
 * unit because (a) the array is short, (b) ordering doesn't matter,
 * (c) edits commonly touch multiple entries (sort by `x`, etc).
 */
export async function updateRunDayDividers(
  db: Firestore,
  pid: string,
  runId: string,
  dividers: readonly RunDayDivider[],
): Promise<void> {
  await updateDoc(runRef(db, pid, runId), {
    'canvasOverrides.dayDividers': dividers.map((d) => ({
      id: d.id,
      x: d.x,
      label: d.label,
      date: d.date,
    })),
    updatedAt: serverTimestamp(),
  });
}

/** Generate a stable short id suitable for array keys. */
export function newDividerId(): string {
  return Math.random().toString(36).slice(2, 10);
}
